import { World } from '../sim/world';
import { neutralInput, type CarInput, type WorldState } from '../sim/types';
import { getTrack } from '../tracks';
import type { Scenario, ScenarioInputEvent } from './types';

/** Construit un World à partir des spawns, murs et circuit d'un scénario. */
export function worldFromScenario(sc: Scenario): World {
  return new World({
    seed: sc.seed,
    cars: sc.cars.map((c) => ({
      id: c.id,
      x: c.spawn.x,
      y: c.spawn.y,
      angle: c.spawn.angle,
      vehicle: c.vehicle,
      ai: c.ai,
    })),
    walls: sc.walls,
    track: getTrack(sc.track),
  });
}

/** Nombre de pas à simuler = max(frame de capture, frame d'input + 1, 1). */
export function stepCount(sc: Scenario): number {
  let n = 1;
  for (const e of sc.inputs) n = Math.max(n, e.frame + 1);
  for (const c of sc.captures) n = Math.max(n, c.frame);
  return n;
}

function applyEvent(target: CarInput, ev: ScenarioInputEvent): void {
  if (ev.throttle !== undefined) target.throttle = ev.throttle;
  if (ev.brake !== undefined) target.brake = ev.brake;
  if (ev.steer !== undefined) target.steer = ev.steer;
  if (ev.handbrake !== undefined) target.handbrake = ev.handbrake;
}

/**
 * Étend les inputs épars en timeline dense `[frame][carIdx]` par sample-and-hold.
 * `out[i]` = inputs actifs pendant le i-ème pas (les événements de frame i sont déjà appliqués).
 */
export function buildInputTimeline(sc: Scenario, carCount: number): CarInput[][] {
  const steps = stepCount(sc);
  const cur: CarInput[] = Array.from({ length: carCount }, () => neutralInput());
  const byFrame = new Map<number, ScenarioInputEvent[]>();
  for (const ev of sc.inputs) {
    const arr = byFrame.get(ev.frame);
    if (arr) arr.push(ev);
    else byFrame.set(ev.frame, [ev]);
  }

  const out: CarInput[][] = [];
  for (let i = 0; i < steps; i++) {
    for (const ev of byFrame.get(i) ?? []) {
      if (ev.car >= 0 && ev.car < carCount) applyEvent(cur[ev.car], ev);
    }
    out.push(cur.map((c) => ({ ...c })));
  }
  return out;
}

export interface CaptureResult {
  name: string;
  frame: number;
  state: WorldState;
}

/**
 * Rejoue un scénario en pur Node et renvoie l'état du monde à chaque point de capture.
 * Ce code est PARTAGÉ avec le harness navigateur : même replay → mêmes chiffres → mêmes pixels.
 */
export function replay(sc: Scenario): Map<string, CaptureResult> {
  const world = worldFromScenario(sc);
  const dt = sc.fixedDtMs / 1000;
  const timeline = buildInputTimeline(sc, sc.cars.length);
  const captureName = new Map<number, string>();
  for (const c of sc.captures) captureName.set(c.frame, c.name);

  const results = new Map<string, CaptureResult>();
  for (let i = 0; i < timeline.length; i++) {
    world.fixedUpdate(dt, timeline[i]);
    const name = captureName.get(world.step);
    if (name) results.set(name, { name, frame: world.step, state: world.getState() });
  }
  return results;
}
